/**
 * Mobile Navigation Tabs
 * Handles tab switching functionality for mobile menu
 */

(function() {
  'use strict';

  const SELECTORS = {
    tabsContainer: '[data-mobile-tabs]',
    tabTrigger: '[data-mobile-tab-trigger]',
    tabContent: '[data-mobile-tab-content]',
    screensContainer: '[data-mobile-screens]',
    screen: '[data-screen]',
    navigateButton: '[data-navigate-to]',
    backButton: '[data-screen-back]',
    activeTabClass: 'mobile-nav-tabs__tab--active',
    activePanelClass: 'mobile-nav-tabs__panel--active',
    activeScreenClass: 'mobile-nav-screen--active',
    enteringScreenClass: 'mobile-nav-screen--entering',
    exitingScreenClass: 'mobile-nav-screen--exiting'
  };

  class MobileNavTabs {
    constructor(container) {
      this.container = container;
      this.tabs = this.container.querySelectorAll(SELECTORS.tabTrigger);
      this.panels = this.container.querySelectorAll(SELECTORS.tabContent);
      this.screensContainers = this.container.querySelectorAll(SELECTORS.screensContainer);
      
      if (this.tabs.length === 0 || this.panels.length === 0) return;
      
      this.init();
    }

    init() {
      this.bindTabEvents();
      this.bindScreenEvents();
      // Set initial active state
      this.setActiveTab(this.tabs[0]);
    }

    bindTabEvents() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', this.handleTabClick.bind(this));
        tab.addEventListener('keydown', this.handleTabKeydown.bind(this));
      });
    }

    bindScreenEvents() {
      // Bind navigation buttons
      this.container.addEventListener('click', (event) => {
        const navigateButton = event.target.closest(SELECTORS.navigateButton);
        if (navigateButton) {
          event.preventDefault();
          this.navigateToScreen(navigateButton);
        }

        const backButton = event.target.closest(SELECTORS.backButton);
        if (backButton) {
          event.preventDefault();
          this.navigateBack(backButton);
        }
      });
    }

    navigateToScreen(button) {
      const targetScreenId = button.getAttribute('data-navigate-to');
      const screensContainer = button.closest(SELECTORS.screensContainer);
      
      if (!screensContainer || !targetScreenId) return;

      const currentScreen = screensContainer.querySelector(`.${SELECTORS.activeScreenClass}`);
      const targetScreen = screensContainer.querySelector(`[data-screen="${targetScreenId}"]`);

      if (!targetScreen || targetScreen === currentScreen) return;

      this.performScreenTransition(currentScreen, targetScreen, 'forward');
    }

    navigateBack(button) {
      const screensContainer = button.closest(SELECTORS.screensContainer);
      
      if (!screensContainer) return;

      const currentScreen = screensContainer.querySelector(`.${SELECTORS.activeScreenClass}`);
      const mainScreen = screensContainer.querySelector('[data-screen="main"]');

      if (!mainScreen || currentScreen === mainScreen) return;

      this.performScreenTransition(currentScreen, mainScreen, 'back');
    }

    performScreenTransition(currentScreen, targetScreen, direction) {
      if (!currentScreen || !targetScreen) return;

      // Set up target screen position
      if (direction === 'forward') {
        targetScreen.style.transform = 'translateX(100%)';
      } else {
        targetScreen.style.transform = 'translateX(-100%)';
      }

      targetScreen.classList.add(SELECTORS.enteringScreenClass);
      targetScreen.style.opacity = '1';
      targetScreen.style.visibility = 'visible';

      // Force reflow
      targetScreen.offsetHeight;

      // Start animation
      requestAnimationFrame(() => {
        currentScreen.classList.add(SELECTORS.exitingScreenClass);
        targetScreen.style.transform = 'translateX(0)';

        if (direction === 'forward') {
          currentScreen.style.transform = 'translateX(-100%)';
        } else {
          currentScreen.style.transform = 'translateX(100%)';
        }
      });

      // Complete transition
      setTimeout(() => {
        // Clean up current screen
        currentScreen.classList.remove(SELECTORS.activeScreenClass, SELECTORS.exitingScreenClass);
        currentScreen.style.transform = '';
        currentScreen.style.opacity = '';
        currentScreen.style.visibility = '';

        // Activate target screen
        targetScreen.classList.remove(SELECTORS.enteringScreenClass);
        targetScreen.classList.add(SELECTORS.activeScreenClass);
        targetScreen.style.transform = '';
      }, 10);
    }

    handleTabClick(event) {
      event.preventDefault();
      const clickedTab = event.currentTarget;
      this.setActiveTab(clickedTab);
    }

    handleTabKeydown(event) {
      // Handle keyboard navigation
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigateTab(event.key === 'ArrowRight' ? 1 : -1);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.setActiveTab(event.currentTarget);
      }
    }

    navigateTab(direction) {
      const activeTab = this.container.querySelector(`.${SELECTORS.activeTabClass}`);
      const activeIndex = Array.from(this.tabs).indexOf(activeTab);
      let newIndex = activeIndex + direction;

      // Wrap around
      if (newIndex < 0) newIndex = this.tabs.length - 1;
      if (newIndex >= this.tabs.length) newIndex = 0;

      this.setActiveTab(this.tabs[newIndex]);
      this.tabs[newIndex].focus();
    }

    setActiveTab(targetTab) {
      const targetId = targetTab.getAttribute('data-mobile-tab-trigger');
      const currentActivePanel = this.container.querySelector(`.${SELECTORS.activePanelClass}`);
      
      // If same tab is clicked, do nothing
      if (targetTab.classList.contains(SELECTORS.activeTabClass)) {
        return;
      }
      
      // Handle panel transitions
      if (currentActivePanel) {
        currentActivePanel.classList.add('mobile-nav-tabs__panel--exiting');
        
        // Wait for exit animation before showing new panel
        setTimeout(() => {
          this.showNewPanel(targetTab, targetId);
          if (currentActivePanel) {
            currentActivePanel.classList.remove('mobile-nav-tabs__panel--exiting');
          }
        }, 300);
      } else {
        this.showNewPanel(targetTab, targetId);
      }
    }

    showNewPanel(targetTab, targetId) {
      // Remove active state from all tabs and panels
      this.tabs.forEach(tab => {
        tab.classList.remove(SELECTORS.activeTabClass);
        tab.setAttribute('aria-selected', 'false');
      });

      this.panels.forEach(panel => {
        panel.classList.remove(SELECTORS.activePanelClass);
        panel.setAttribute('aria-hidden', 'true');
      });

      // Add active state to target tab and panel
      targetTab.classList.add(SELECTORS.activeTabClass);
      targetTab.setAttribute('aria-selected', 'true');

      const targetPanel = this.container.querySelector(`[data-mobile-tab-content="${targetId}"]`);
      if (targetPanel) {
        targetPanel.classList.add(SELECTORS.activePanelClass);
        targetPanel.setAttribute('aria-hidden', 'false');
        
        // Reset screens to main screen when switching tabs
        const screensContainer = targetPanel.querySelector(SELECTORS.screensContainer);
        if (screensContainer) {
          const screens = screensContainer.querySelectorAll(SELECTORS.screen);
          const mainScreen = screensContainer.querySelector('[data-screen="main"]');
          
          screens.forEach(screen => {
            screen.classList.remove(SELECTORS.activeScreenClass, SELECTORS.enteringScreenClass, SELECTORS.exitingScreenClass);
            screen.style.transform = '';
            screen.style.opacity = '';
            screen.style.visibility = '';
          });
          
          if (mainScreen) {
            mainScreen.classList.add(SELECTORS.activeScreenClass);
          }
        }
        
        // Trigger a custom event for tab change
        targetPanel.dispatchEvent(new CustomEvent('mobileTabChange', {
          bubbles: true,
          detail: { 
            tabId: targetId, 
            panel: targetPanel,
            tab: targetTab 
          }
        }));
      }

      // Scroll active tab into view if needed
      this.scrollTabIntoView(targetTab);
    }

    scrollTabIntoView(tab) {
      const tabsHeader = tab.closest('.mobile-nav-tabs__headers');
      if (!tabsHeader) return;

      const tabLeft = tab.offsetLeft;
      const tabWidth = tab.offsetWidth;
      const headerScrollLeft = tabsHeader.scrollLeft;
      const headerWidth = tabsHeader.offsetWidth;

      // Check if tab is fully visible
      if (tabLeft < headerScrollLeft || tabLeft + tabWidth > headerScrollLeft + headerWidth) {
        // Scroll to center the tab
        const scrollTo = tabLeft - (headerWidth / 2) + (tabWidth / 2);
        tabsHeader.scrollTo({
          left: Math.max(0, scrollTo),
          behavior: 'smooth'
        });
      }
    }

    destroy() {
      this.tabs.forEach(tab => {
        tab.removeEventListener('click', this.handleTabClick);
        tab.removeEventListener('keydown', this.handleTabKeydown);
      });
      
      // Clean up screen event listeners
      this.container.removeEventListener('click', this.handleScreenClick);
    }
  }

  // Initialize when DOM is ready
  function initMobileNavTabs() {
    const tabContainers = document.querySelectorAll(SELECTORS.tabsContainer);
    
    tabContainers.forEach(container => {
      // Avoid double initialization
      if (!container.dataset.mobileTabsInitialized) {
        container.dataset.mobileTabsInitialized = 'true';
        new MobileNavTabs(container);
      }
    });
  }

  // Initialize on DOM content loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileNavTabs);
  } else {
    initMobileNavTabs();
  }

  // Re-initialize when mobile menu is opened (in case content changes)
  document.addEventListener('theme:drawer:open', function(event) {
    if (event.detail && event.detail.drawer && event.detail.drawer.classList.contains('drawer--nav')) {
      // Small delay to ensure content is rendered
      setTimeout(initMobileNavTabs, 100);
    }
  });

  // Also listen for any dynamic content changes
  if (window.MutationObserver) {
    const observer = new MutationObserver(function(mutations) {
      let shouldInit = false;
      
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1 && node.querySelector && node.querySelector(SELECTORS.tabsContainer)) {
            shouldInit = true;
          }
        });
      });
      
      if (shouldInit) {
        initMobileNavTabs();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

})();
